// Users (SM-754): search by id / name / status / client, and create a
// sandbox user (id optional — the mock generates one; immutable after).
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Field, Select, TextInput } from "@/components/ui/Field";
import { Empty, PageHeader, ReadOnlyHint, TableWrap } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { checkAmount } from "@/lib/amount";
import { ACCOUNT_STATUSES as STATUSES } from "@/lib/constants";
import { api } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";
import { useCanMutate } from "@/lib/session";
import type { User } from "@/lib/types";

interface UsersSearch {
  q?: string;
  status?: string;
  client_id?: string;
}

export const Route = createFileRoute("/_app/users/")({
  validateSearch: (s: Record<string, unknown>): UsersSearch => ({
    q: typeof s.q === "string" && s.q ? s.q : undefined,
    status: typeof s.status === "string" && s.status ? s.status : undefined,
    client_id: typeof s.client_id === "string" && s.client_id ? s.client_id : undefined,
  }),
  component: UsersPage,
});

function UsersPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/users/" });
  const [q, setQ] = useState(search.q ?? "");
  const params = { q: search.q, status: search.status, client_id: search.client_id };
  const users = useQuery({ queryKey: keys.users(params), queryFn: () => fetchers.users(params) });
  const clients = useQuery({ queryKey: keys.clients(), queryFn: fetchers.clients });
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Sandbox provider users and their wallet balances. A user id is immutable once created and is never recycled."
        actions={
          <Button onClick={() => setCreating((v) => !v)} variant={creating ? "ghost" : "primary"}>
            {creating ? "Close form" : "Create user"}
          </Button>
        }
      />
      {creating && <CreateUser clients={(clients.data ?? []).map((c) => c.client_id)} onCreated={(u) => navigate({ to: "/users/$id", params: { id: u.user_id } })} />}

      <form
        className="card mb-4 grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          navigate({ search: (s: UsersSearch) => ({ ...s, q: q.trim() || undefined }) });
        }}
      >
        <Field label="Search id or name">
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="mock_user_0001, Funded…" />
        </Field>
        <Field label="Status">
          <Select
            value={search.status ?? ""}
            onChange={(e) => navigate({ search: (s: UsersSearch) => ({ ...s, status: e.target.value || undefined }) })}
          >
            <option value="">Any status</option>
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </Field>
        <Field label="Client">
          <Select
            value={search.client_id ?? ""}
            onChange={(e) => navigate({ search: (s: UsersSearch) => ({ ...s, client_id: e.target.value || undefined }) })}
          >
            <option value="">Any client</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.client_id}>{c.client_id}</option>
            ))}
          </Select>
        </Field>
        <Button type="submit">Search</Button>
      </form>

      <ErrorBanner error={users.error} />
      {users.isLoading ? (
        <Skeleton lines={6} />
      ) : (users.data ?? []).length === 0 ? (
        <Empty>No users match.</Empty>
      ) : (
        <TableWrap>
          <table className="tbl">
            <thead>
              <tr>
                <th>User id</th>
                <th>Name</th>
                <th>Client</th>
                <th>Status</th>
                <th className="text-right">Available</th>
                <th>Created by</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {(users.data ?? []).map((u) => (
                <tr key={u.user_id}>
                  <td>
                    <Link to="/users/$id" params={{ id: u.user_id }} className="mono">
                      {u.user_id}
                    </Link>
                  </td>
                  <td>{u.display_name || <span className="t-mute">—</span>}</td>
                  <td className="mono">{u.client_id}</td>
                  <td>
                    <StatusBadge status={u.account_status} />
                  </td>
                  <td className="amt text-right">{u.available_balance}</td>
                  <td>{u.created_by}</td>
                  <td>{fmtTime(u.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function CreateUser({ clients, onCreated }: { clients: string[]; onCreated: (u: User) => void }) {
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const [userId, setUserId] = useState("");
  const [clientId, setClientId] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState<string>("ACTIVE");
  const [opening, setOpening] = useState("0");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const client = clientId || clients[0] || "";
  const amount = checkAmount(opening, { allowZero: true });
  const reasonOk = reason.trim().length >= 3;
  const idOk = userId === "" || /^[A-Za-z0-9._:-]{3,64}$/.test(userId);

  return (
    <SectionCard title="Create user" className="mb-4">
      {!canMutate && <ReadOnlyHint />}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!amount.ok || !reasonOk || !idOk || !client) return;
          setBusy(true);
          setError(null);
          try {
            const u = await api<User>("/users", {
              method: "POST",
              body: {
                user_id: userId.trim(),
                client_id: client,
                display_name: name.trim(),
                account_status: status,
                opening_balance: amount.value,
                reason: reason.trim(),
              },
            });
            await qc.invalidateQueries({ queryKey: ["mock", "users"] });
            onCreated(u);
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset disabled={!canMutate || busy}>
          <div className="form-grid">
            <Field label="User id (optional)" hint="Leave empty to generate one. 3–64 of A–Z a–z 0–9 . _ : -" error={idOk ? null : "Invalid user id."}>
              <TextInput value={userId} onChange={(e) => setUserId(e.target.value)} className="mono" placeholder="generated" />
            </Field>
            <Field label="Client">
              <Select value={client} onChange={(e) => setClientId(e.target.value)}>
                {clients.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </Field>
            <Field label="Display name">
              <TextInput value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Status">
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </Select>
            </Field>
            <Field label="Opening balance" hint="0–4 decimal places" error={amount.ok ? null : amount.error}>
              <TextInput value={opening} onChange={(e) => setOpening(e.target.value)} inputMode="decimal" className="mono" />
            </Field>
            <Field label="Reason (required)" error={reason && !reasonOk ? "At least 3 characters." : null} className="span-all">
              <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="why this test user exists" />
            </Field>
          </div>
          <ErrorBanner error={error} className="mt-3" />
          <div className="mt-4 flex justify-end">
            <Button type="submit" loading={busy} disabled={!amount.ok || !reasonOk || !idOk || !client}>
              Create user
            </Button>
          </div>
        </fieldset>
      </form>
    </SectionCard>
  );
}
